import { create } from 'zustand'

export interface Layer {
  id: string
  type: 'image' | 'text'
  name: string
  visible: boolean
  locked: boolean
  x: number
  y: number
  width: number
  height: number
  rotation: number
  opacity: number
  // Image specific
  assetId?: string
  imageUrl?: string
  // Text specific
  content?: string
  fontFamily?: string
  fontSize?: number
  fontColor?: string
  fontWeight?: number
  textAlign?: 'left' | 'center' | 'right'
  stroke?: { color: string; width: number }
  shadow?: { color: string; blur: number; offsetX: number; offsetY: number }
}

export interface Cover {
  id: string
  title: string
  width: number
  height: number
  backgroundColor: string
  layers: Layer[]
  createdAt: string
  updatedAt: string
}

interface EditorState {
  cover: Cover | null
  selectedLayerId: string | null
  history: Cover[]
  historyIndex: number
  zoom: number
  tool: 'select' | 'move' | 'text'
  isSaving: boolean
  hasUnsavedChanges: boolean

  // Actions
  setCover: (cover: Cover) => void
  setSelectedLayer: (id: string | null) => void
  addLayer: (layer: Omit<Layer, 'id'>) => void
  updateLayer: (id: string, updates: Partial<Layer>) => void
  removeLayer: (id: string) => void
  reorderLayers: (fromIndex: number, toIndex: number) => void
  setZoom: (zoom: number) => void
  setTool: (tool: EditorState['tool']) => void
  undo: () => void
  redo: () => void
  saveSnapshot: () => void
  resetEditor: () => void
}

const generateId = () => Math.random().toString(36).substring(2, 9)

export const useEditorStore = create<EditorState>((set, get) => ({
  cover: null,
  selectedLayerId: null,
  history: [],
  historyIndex: -1,
  zoom: 1,
  tool: 'select',
  isSaving: false,
  hasUnsavedChanges: false,

  setCover: (cover) => {
    set({ cover, history: [cover], historyIndex: 0, hasUnsavedChanges: false })
  },

  setSelectedLayer: (id) => set({ selectedLayerId: id }),

  addLayer: (layerData) => {
    const state = get()
    if (!state.cover) return

    const layer: Layer = {
      ...layerData,
      id: generateId(),
    }

    const newCover = {
      ...state.cover,
      layers: [...state.cover.layers, layer],
      updatedAt: new Date().toISOString(),
    }

    set({ cover: newCover, selectedLayerId: layer.id, hasUnsavedChanges: true })
    get().saveSnapshot()
  },

  updateLayer: (id, updates) => {
    const state = get()
    if (!state.cover) return

    const newLayers = state.cover.layers.map((layer) =>
      layer.id === id ? { ...layer, ...updates } : layer
    )

    const newCover = {
      ...state.cover,
      layers: newLayers,
      updatedAt: new Date().toISOString(),
    }

    set({ cover: newCover, hasUnsavedChanges: true })
  },

  removeLayer: (id) => {
    const state = get()
    if (!state.cover) return

    const newLayers = state.cover.layers.filter((layer) => layer.id !== id)
    const newCover = {
      ...state.cover,
      layers: newLayers,
      updatedAt: new Date().toISOString(),
    }

    set({
      cover: newCover,
      selectedLayerId: state.selectedLayerId === id ? null : state.selectedLayerId,
      hasUnsavedChanges: true,
    })
    get().saveSnapshot()
  },

  reorderLayers: (fromIndex, toIndex) => {
    const state = get()
    if (!state.cover) return

    const newLayers = [...state.cover.layers]
    const [removed] = newLayers.splice(fromIndex, 1)
    newLayers.splice(toIndex, 0, removed)

    const newCover = {
      ...state.cover,
      layers: newLayers,
      updatedAt: new Date().toISOString(),
    }

    set({ cover: newCover, hasUnsavedChanges: true })
    get().saveSnapshot()
  },

  setZoom: (zoom) => set({ zoom: Math.min(3, Math.max(0.1, zoom)) }),

  setTool: (tool) => set({ tool }),

  undo: () => {
    const state = get()
    if (state.historyIndex > 0) {
      const newIndex = state.historyIndex - 1
      set({
        cover: state.history[newIndex],
        historyIndex: newIndex,
        hasUnsavedChanges: true,
      })
    }
  },

  redo: () => {
    const state = get()
    if (state.historyIndex < state.history.length - 1) {
      const newIndex = state.historyIndex + 1
      set({
        cover: state.history[newIndex],
        historyIndex: newIndex,
        hasUnsavedChanges: true,
      })
    }
  },

  saveSnapshot: () => {
    const state = get()
    if (!state.cover) return

    const newHistory = state.history.slice(0, state.historyIndex + 1)
    newHistory.push({ ...state.cover })

    // Keep only last 50 snapshots
    if (newHistory.length > 50) {
      newHistory.shift()
    }

    set({
      history: newHistory,
      historyIndex: newHistory.length - 1,
    })
  },

  resetEditor: () => {
    set({
      cover: null,
      selectedLayerId: null,
      history: [],
      historyIndex: -1,
      zoom: 1,
      tool: 'select',
      hasUnsavedChanges: false,
    })
  },
}))
